/**
 * The active site's lift cars with their building facts, from W1's asset
 * (`liftFactsFromAsset(twinAssetFor(site).lifts)`): what the lift drawer's
 * callers hand it, so its Guests and Robots lines are live wherever it
 * opens (twin slice 1, W6).
 *
 * The asset is the only source of all six cars and of the robot map's
 * lanes through a car robots are not meant to use; the server's robot
 * roster names only the robot cars (`useLiftColumnState`). Nothing is
 * hardcoded: when a map drop removes those lanes and the asset is
 * re-exported, the hedged site row and the ops line go with them.
 *
 * NULL UNTIL KNOWN. While the asset loads, when the site has none, when
 * it is not this site's (`loadGeometry`'s site check) or when it fails to
 * load, the answer is null and the drawer shows no lines -- never
 * guessed ones. A result is kept with the site and asset it was read
 * for, so after a site switch the previous site's cars are never handed
 * out, not even for one render. `loadGeometry` fetches each asset once
 * per session, so the drawer, the building view and the Devices screen
 * share one request.
 */
import { useEffect, useState } from 'react';
import { useSafeSiteId } from '../../state/useActiveSite';
import { loadGeometry } from '../Twin/geometry';
import { twinAssetFor } from '../Twin/twin-assets';
import { liftFactsFromAsset } from './lift-column-state';
import type { LiftRobotFacts } from './lift-reading';

export function useSiteLiftCars(enabled = true): readonly LiftRobotFacts[] | null {
  const siteId = useSafeSiteId();
  const asset = twinAssetFor(siteId);
  const key = enabled && asset ? `${asset.site} ${asset.url}` : null;
  const [loaded, setLoaded] = useState<{ key: string; cars: LiftRobotFacts[] } | null>(null);

  useEffect(() => {
    if (key === null || asset === null) return;
    let cancelled = false;
    loadGeometry(asset.url, asset.site).then(
      (geometry) => {
        if (!cancelled) setLoaded({ key, cars: liftFactsFromAsset(geometry.lifts) });
      },
      () => {
        // No building facts: the drawer opens without its lines.
      },
    );
    return () => {
      cancelled = true;
    };
    // `asset` is derived from the site and is the same pair as `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return loaded !== null && loaded.key === key ? loaded.cars : null;
}
